// packages/shared/src/metrics.ts
//
// The single source of truth for what SkinVizo measures.
// Both @skinvizo/scoring and @skinvizo/recommender read from here, as do
// the widget report UI and the dashboard's concern-mapping screen. Adding a
// metric means adding it once, here.

export type MetricKey =
  | 'spots'
  | 'wrinkles'
  | 'oiliness'
  | 'moisture'
  | 'radiance'
  | 'acne'
  | 'pores'
  | 'texture'
  | 'firmness'
  | 'redness'
  | 'pigmentation'
  | 'darkCircles'
  | 'blackheads'
  | 'eyeBag'
  | 'tearTrough';

export type FaceRegion =
  | 'forehead'
  | 'glabella'
  | 'nose'
  | 'leftCheek'
  | 'rightCheek'
  | 'periorbital'
  | 'infraorbital'
  | 'upperEyelid'
  | 'lowerEyelid'
  | 'nasolabial'
  | 'perioral'
  | 'chin'
  | 'jawline'
  | 'tzone'
  | 'fullFace';

export interface MetricDefinition {
  key: MetricKey;
  label: string;
  /** Contribution to the overall Skin Score (weighted mean). */
  weight: number;
  /** Contribution to the Skin Age model. Zero means it does not age the face. */
  ageWeight: number;
  /** Concern key consumed by the recommender when matching treatments. */
  concern: string;
  /** Regions the provider is asked to report on. */
  regions: FaceRegion[];
  /** True when the metric is meaningfully affected by age banding. */
  ageBanded: boolean;
}

export const METRICS: Record<MetricKey, MetricDefinition> = {
  spots: {
    key: 'spots', label: 'Spots', weight: 1.0, ageWeight: 3.0,
    concern: 'spots', regions: ['leftCheek', 'rightCheek', 'forehead', 'nose'], ageBanded: true,
  },
  wrinkles: {
    key: 'wrinkles', label: 'Wrinkles', weight: 1.2, ageWeight: 6.0,
    concern: 'wrinkles', regions: ['forehead', 'glabella', 'periorbital', 'nasolabial'], ageBanded: true,
  },
  oiliness: {
    key: 'oiliness', label: 'Oiliness', weight: 1.0, ageWeight: 0,
    concern: 'excess_sebum', regions: ['tzone', 'forehead', 'nose'], ageBanded: true,
  },
  moisture: {
    key: 'moisture', label: 'Moisture', weight: 1.0, ageWeight: -2.0,
    concern: 'dehydration', regions: ['fullFace'], ageBanded: false,
  },
  radiance: {
    key: 'radiance', label: 'Radiance', weight: 0.9, ageWeight: 2.0,
    concern: 'dullness', regions: ['fullFace'], ageBanded: false,
  },
  acne: {
    key: 'acne', label: 'Acne', weight: 1.1, ageWeight: 0,
    concern: 'acne', regions: ['forehead', 'leftCheek', 'rightCheek', 'chin'], ageBanded: true,
  },
  pores: {
    key: 'pores', label: 'Pores', weight: 0.9, ageWeight: 1.0,
    concern: 'enlarged_pores', regions: ['nose', 'leftCheek', 'rightCheek'], ageBanded: false,
  },
  texture: {
    key: 'texture', label: 'Texture', weight: 0.9, ageWeight: 1.5,
    concern: 'rough_texture', regions: ['fullFace'], ageBanded: false,
  },
  firmness: {
    key: 'firmness', label: 'Firmness', weight: 1.1, ageWeight: 4.0,
    concern: 'laxity', regions: ['jawline', 'leftCheek', 'rightCheek'], ageBanded: true,
  },
  redness: {
    key: 'redness', label: 'Redness', weight: 0.8, ageWeight: 0,
    concern: 'sensitivity', regions: ['leftCheek', 'rightCheek', 'nose'], ageBanded: false,
  },
  pigmentation: {
    key: 'pigmentation', label: 'Pigmentation', weight: 1.0, ageWeight: 2.0,
    concern: 'uneven_tone', regions: ['forehead', 'leftCheek', 'rightCheek', 'perioral'], ageBanded: true,
  },
  darkCircles: {
    key: 'darkCircles', label: 'Dark Circles', weight: 0.9, ageWeight: 1.0,
    concern: 'periorbital_darkening', regions: ['periorbital'], ageBanded: false,
  },
  blackheads: {
    key: 'blackheads', label: 'Blackheads', weight: 0.7, ageWeight: 0,
    concern: 'comedones', regions: ['nose', 'chin', 'forehead'], ageBanded: false,
  },

  // Periorbital volume metrics. Both are structural rather than surface findings,
  // so they carry a lower Skin Score weight than the skin metrics above — a
  // frontal photo reads them from shadow, and shadow is exactly what the
  // preprocessing normaliser is built to flatten. See analysisSurface below.
  // Their age weight is high because hollowing and fat prolapse are among the
  // strongest visual ageing signals a patient recognises in their own face.
  eyeBag: {
    key: 'eyeBag', label: 'Eye Bags', weight: 0.8, ageWeight: 2.0,
    concern: 'orbital_fat_prolapse', regions: ['infraorbital', 'lowerEyelid'], ageBanded: true,
  },
  tearTrough: {
    key: 'tearTrough', label: 'Tear Trough', weight: 0.8, ageWeight: 2.5,
    concern: 'tear_trough_hollowing', regions: ['infraorbital'], ageBanded: true,
  },
};

/**
 * Metrics that must be observed on the pre-normalised image.
 *
 * Preprocessing applies gray-world white balance and CLAHE-style local contrast
 * normalisation so that scores are comparable across bathrooms. That is correct
 * for every surface metric and destructive for the volume ones: tear trough
 * depth and eye bag protrusion are visible only as a shadow gradient, and
 * flattening uneven luminance is precisely what the normaliser does.
 *
 * The provider is given both buffers and told which metrics to read from which.
 */
export const PRE_NORMALISATION_METRICS: MetricKey[] = ['eyeBag', 'tearTrough'];

export const METRIC_KEYS = Object.keys(METRICS) as MetricKey[];

/**
 * Derived readings — computed by the scoring engine rather than observed
 * directly, and reported alongside the metrics above.
 *
 *   skinScore    weighted mean of all enabled metrics
 *   skinAge      declaredAge adjusted by ageWeight, clamped to +/- 12
 *   skinType     normal | dry | oily | combination, from oiliness + moisture
 *   fitzpatrick  I-VI phototype — gates energy-based treatment recommendations
 */
export const DERIVED_KEYS = ['skinScore', 'skinAge', 'skinType', 'fitzpatrick'] as const;

/** Observation severity as returned by the analysis provider. Never a score. */
export type Severity = 0 | 1 | 2 | 3 | 4;

export const SEVERITY_LABELS: Record<Severity, string> = {
  0: 'none',
  1: 'minimal',
  2: 'mild',
  3: 'moderate',
  4: 'marked',
};

export type Band = 'fair' | 'average' | 'good';

export const BAND_THRESHOLDS = { fair: 0, average: 70, good: 88 } as const;

/** Which side a finding appears on. Asymmetry is itself clinically meaningful. */
export type Laterality = 'left' | 'right' | 'bilateral';

// ============================================================
//  ANATOMY FLAGS
// ============================================================
//
// Observed findings that describe facial ANATOMY rather than skin condition.
// They are deliberately not metrics:
//
//   - they never contribute to Skin Score or Skin Age. A skin-health number
//     that moves because of eyelid position is not measuring skin health, and
//     a clinic cannot explain it to a patient who asks.
//   - they are never simulated (see NON_SIMULABLE_REGIONS).
//   - a positive finding may route to a referral rather than a treatment.
//
// They still reach the recommender and the patient record, which is the point:
// an injector planning a brow treatment needs to know about lid position before
// the needle, not after.

export type AnatomyFlagKey = 'droopyUpperEyelid' | 'droopyLowerEyelid';

export interface AnatomyFlagDefinition {
  key: AnatomyFlagKey;
  label: string;
  /** Concern key consumed by the recommender. */
  concern: string;
  regions: FaceRegion[];
  /** Shown in the patient-facing report. False means staff-only. */
  patientVisible: boolean;
  /**
   * A positive finding produces a referral note instead of a treatment
   * recommendation. Set where the finding may be medical rather than cosmetic.
   */
  referralOnPositive: boolean;
  /** Shown to staff beside the finding, so the flag is never read as a diagnosis. */
  clinicalNote: string;
}

export const ANATOMY_FLAGS: Record<AnatomyFlagKey, AnatomyFlagDefinition> = {
  droopyUpperEyelid: {
    key: 'droopyUpperEyelid',
    label: 'Upper Eyelid Position',
    concern: 'upper_lid_hooding',
    regions: ['upperEyelid', 'periorbital'],
    // Staff-only, and this is not a UI preference. True ptosis can be
    // neurological — Horner's, myasthenia gravis, third-nerve palsy — and is
    // also a recognised complication of botulinum toxin, which this product
    // recommends. Telling a patient their eyelid droops, inside a report that
    // states it is not a diagnosis, is the wrong place to raise it.
    patientVisible: false,
    referralOnPositive: true,
    clinicalNote:
      'Distinguish cosmetic upper-lid hooding (dermatochalasis, skin laxity) from true ptosis. ' +
      'Unilateral or recent-onset findings warrant referral before any periorbital treatment.',
  },
  droopyLowerEyelid: {
    key: 'droopyLowerEyelid',
    label: 'Lower Eyelid Position',
    concern: 'lower_lid_laxity',
    regions: ['lowerEyelid', 'infraorbital'],
    // Lower lid laxity and scleral show are cosmetic findings, so the patient
    // sees this one.
    patientVisible: true,
    referralOnPositive: false,
    clinicalNote:
      'Lower lid laxity increases the risk of malar oedema after tear trough filler. ' +
      'Check before recommending infraorbital volume.',
  },
};

export const ANATOMY_FLAG_KEYS = Object.keys(ANATOMY_FLAGS) as AnatomyFlagKey[];

/**
 * Regions the simulation engine must never edit.
 *
 * Eyelid position is out because upper lid ptosis is a recognised complication
 * of botulinum toxin and this product recommends it. Rendering a "corrected"
 * eyelid would be both a medical claim and a promise about an outcome the
 * injector cannot deliver. Infraorbital is NOT on this list — tear trough
 * filler is an ordinary cosmetic treatment and previewing it is legitimate.
 */
export const NON_SIMULABLE_REGIONS: FaceRegion[] = ['upperEyelid', 'lowerEyelid'];
